import { apiRequest } from './apiClient.js';
import { hydrateRunningBotTimer } from '../utils/botRuntimeTimer.js';
import { consumePrefetchedBots, currentBotIdentity, prefetchBots } from './botStartupPrefetch.js';
import { withoutUnconfirmedRuntime } from '../utils/botBootstrapPresentation.js';

/**
 * Bots Service
 * Handles operations with bots
 */
class BotsService {
    async _request(path, options = {}) {
        try {
            return await apiRequest(path, options);
        } catch (error) {
            if (error.status || error.name === 'AbortError') throw error;
            throw this._createError(error.message || 'Network error', 0);
        }
    }
    /**
     * Get all bots for the current user
     * @returns {Promise<Array>} Array of bots
     */
    async getBots() {
        return this._request('/bots', { method: 'GET' });
    }

    async getBotsBootstrap() {
        const cached = consumePrefetchedBots();
        if (cached) return cached;
        const identity = currentBotIdentity();
        if (!identity) return this.loadBotsBootstrap();
        prefetchBots(() => this.loadBotsBootstrap(), identity);
        return consumePrefetchedBots();
    }

    prefetchBootstrap(identity) {
        prefetchBots(() => this.loadBotsBootstrap(), identity);
    }

    async loadBotsBootstrap() {
        const data = await this._request('/bots/bootstrap', {
            method: 'GET',
            cache: 'no-store',
            headers: { 'Cache-Control': 'no-cache' },
        });
        return Array.isArray(data?.bots) ? data.bots.map(withoutUnconfirmedRuntime).map(bot => bot.status === 'running'
            // Bootstrap already includes the persisted run boundary; runtime summaries may arrive later.
            ? hydrateRunningBotTimer({ ...bot, started_at: bot.started_at ?? bot.last_run_started_at })
            : bot) : [];
    }

    async getRuntimeSummary(botIds) {
        const data = await this._request('/bots/runtime-summary', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ botIds }),
            responseContract: 'runtime_summaries_response'
        });
        return data?.summaries && typeof data.summaries === 'object' ? data.summaries : {};
    }

    async getBot(botId) {
        return this._request(`/bots/${encodeURIComponent(botId)}`, { method: 'GET' });
    }

    async createBot(payload) {
        return this._request('/bots', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
    }

    async previewDcaBot(payload, { signal } = {}) {
        const response = await this._request('/bots/dca/preview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal,
            responseContract: 'dca_preview_response'
        });
        return response.preview;
    }

    async previewDcaCycleBudget(payload, { signal } = {}) {
        const response = await this._request('/bots/dca/cycle-budget-preview', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal,
            responseContract: 'dca_cycle_budget_response'
        });
        return response.preview;
    }

    async previewDcaOrderPlan(payload, { signal } = {}) {
        const response = await this._request('/bots/dca/order-plan-preview', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload), signal, responseContract: 'dca_order_plan_response'
        });
        return response.preview;
    }

    async updateBot(botId, payload) {
        return this._request(`/bots/${encodeURIComponent(botId)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
    }

    /**
     * Create a formatted error
     * @private
     * @param {string} message - Error message
     * @param {number} status - HTTP status code
     * @returns {Error} Formatted error
     */
    _createError(message, status, data = null) {
        const error = new Error(message);
        error.status = status;
        error.data = data;
        error.code = data && typeof data === 'object' ? data.code || null : null;
        return error;
    }
}

export default new BotsService();
